import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { apiClient } from '../../../lib/api';
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  BarChart3,
  Minus,
} from 'lucide-react';
import { useToastContext } from '../../../contexts/ToastContext';
import { Spinner } from '../../ui/Spinner';
import { colors } from '../../../styles/tokens';

interface SubscriptionsByTier {
  total: number;
  starter: number;
  pro: number;
  growth: number;
  enterprise: number;
}

interface DailyMetric {
  date: string;
  signups: number;
  datasets_created: number;
  emails_sent: number;
  credits_used: number;
  subscriptions: SubscriptionsByTier;
  mrr: number;
  topups: number;
}

interface CumulativeMetric extends DailyMetric {
  cumulative_signups: number;
  cumulative_datasets: number;
  cumulative_emails: number;
  cumulative_credits: number;
  cumulative_subscriptions: SubscriptionsByTier;
  cumulative_mrr: number;
  cumulative_topups: number;
}

type ViewMode = 'daily' | 'cumulative';

type MetricKey = 'signups' | 'datasets' | 'emails' | 'credits' | 'subscriptions' | 'mrr' | 'topups';

// Metric color definitions — sourced from design tokens
const METRIC_COLORS = {
  signups: colors.chart.blue,
  datasets: colors.chart.purple,
  emails: colors.chart.green,
  credits: colors.chart.red,
  subscriptions: colors.chart.amber,
  mrr: colors.chart.emerald,
  topups: colors.chart.cyan,
} as const;

// Chart data shape for Recharts
interface ChartDataPoint {
  dateLabel: string;
  rawDate: string;
  value: number;
}

// Custom tooltip for line charts
interface MetricTooltipProps {
  active?: boolean;
  payload?: ReadonlyArray<{ value?: number }>;
  label?: string;
  isCurrency?: boolean;
}

const MetricTooltip = React.memo(({ active, payload, label, isCurrency }: MetricTooltipProps) => {
  if (active && payload && payload.length > 0) {
    const value = payload[0].value ?? 0;
    const formatted = isCurrency
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value)
      : value.toLocaleString();

    return (
      <div className="bg-white border border-neutral-200 rounded-md shadow-card px-3 py-2">
        <p className="text-xs text-neutral-500 mb-1">{label}</p>
        <p className="text-sm font-semibold text-neutral-900">{formatted}</p>
      </div>
    );
  }
  return null;
});

interface MetricChartProps {
  title: string;
  data: ChartDataPoint[];
  color: string;
  isCurrency?: boolean;
}

const MetricChart: React.FC<MetricChartProps> = ({ title, data, color, isCurrency }) => {
  const gradientId = useMemo(
    () => `metric-gradient-${title.toLowerCase().replace(/\s+/g, '-')}`,
    [title]
  );

  const formatYAxis = (value: number) => {
    if (isCurrency) {
      if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
      return `$${value}`;
    }
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
    return String(value);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tooltipContent = useCallback(
    (props: any) => (
      <MetricTooltip
        active={props.active}
        payload={props.payload}
        label={props.label}
        isCurrency={isCurrency}
      />
    ),
    [isCurrency]
  );

  return (
    <div
      className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4 hover:shadow-md transition-shadow duration-75"
      aria-label={`${title} line chart`}
      role="tabpanel"
    >
      <h3 className="text-sm font-semibold text-neutral-700 mb-3">{title}</h3>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.15} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} vertical={false} />
          <XAxis
            dataKey="dateLabel"
            tick={{ fontSize: 11, fill: colors.neutral[500] }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tickFormatter={formatYAxis}
            tick={{ fontSize: 11, fill: colors.neutral[500] }}
            tickLine={false}
            axisLine={false}
            width={40}
          />
          <Tooltip content={tooltipContent} />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={{ r: 3, fill: color, strokeWidth: 0 }}
            activeDot={{ r: 5, fill: color, strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Parses YYYY-MM-DD as local date to avoid UTC timezone shift
const parseLocalDate = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDateLabel = (dateStr: string) =>
  parseLocalDate(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const formatTableDate = (dateStr: string) =>
  parseLocalDate(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export const DailyMetricsTab: React.FC = () => {
  const [allMetrics, setAllMetrics] = useState<DailyMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [viewMode, setViewMode] = useState<ViewMode>('daily');
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>('signups');
  const { showToast } = useToastContext();

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch days + 1 to have one extra day for day-over-day comparison
      const response = await apiClient.getDailyMetrics(days + 1);
      setAllMetrics(response.data || []);
    } catch (err: unknown) {
      console.error('Error fetching daily metrics:', err);
      const error = err as { response?: { data?: { detail?: string } } };
      showToast(error.response?.data?.detail || 'Failed to load metrics', 'error');
    } finally {
      setLoading(false);
    }
  }, [days, showToast]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Backend returns data in ascending order (oldest first)
  // slice(-days) gets the most recent N days for display
  // The extra day (first element) is kept in allMetrics for day-over-day comparison lookup
  const metrics = useMemo(() => {
    if (allMetrics.length === 0) {
      return [];
    }
    return allMetrics.slice(-days); // Most recent days (ascending)
  }, [allMetrics, days]);

  // Reversed metrics for table display (newest first)
  const displayMetrics = useMemo(() => [...metrics].reverse(), [metrics]);

  // Calculate totals - same for both modes - MUST be before conditional returns
  const totals = useMemo(
    () =>
      metrics.reduce(
        (acc, day) => ({
          signups: acc.signups + day.signups,
          datasets: acc.datasets + day.datasets_created,
          emails: acc.emails + day.emails_sent,
          credits: acc.credits + day.credits_used,
          subscriptions: {
            total: acc.subscriptions.total + (day.subscriptions?.total || 0),
            starter: acc.subscriptions.starter + (day.subscriptions?.starter || 0),
            pro: acc.subscriptions.pro + (day.subscriptions?.pro || 0),
            growth: acc.subscriptions.growth + (day.subscriptions?.growth || 0),
            enterprise: acc.subscriptions.enterprise + (day.subscriptions?.enterprise || 0),
          },
          mrr: acc.mrr + (day.mrr || 0),
          topups: acc.topups + (day.topups || 0),
        }),
        {
          signups: 0,
          datasets: 0,
          emails: 0,
          credits: 0,
          subscriptions: { total: 0, starter: 0, pro: 0, growth: 0, enterprise: 0 },
          mrr: 0,
          topups: 0,
        }
      ),
    [metrics]
  );

  // Calculate displayed values based on view mode
  const displayedValues = useMemo(() => {
    if (viewMode === 'cumulative') {
      // Show totals for cumulative view
      return totals;
    } else {
      // Show most recent day for daily view
      if (metrics.length === 0) {
        return {
          signups: 0,
          datasets: 0,
          emails: 0,
          credits: 0,
          subscriptions: { total: 0, starter: 0, pro: 0, growth: 0, enterprise: 0 },
          mrr: 0,
          topups: 0,
        };
      }
      const mostRecent = metrics[metrics.length - 1];
      return {
        signups: mostRecent.signups,
        datasets: mostRecent.datasets_created,
        emails: mostRecent.emails_sent,
        credits: mostRecent.credits_used,
        subscriptions: mostRecent.subscriptions || { total: 0, starter: 0, pro: 0, growth: 0, enterprise: 0 },
        mrr: mostRecent.mrr || 0,
        topups: mostRecent.topups || 0,
      };
    }
  }, [metrics, totals, viewMode]);

  // Calculate rate of change (day-over-day: most recent day vs previous day)
  const rateOfChange = useMemo(() => {
    const calculateChange = (current: number, previous: number) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    };

    // Get the most recent day and the day before it
    if (metrics.length < 1) {
      return { signups: 0, datasets: 0, emails: 0, credits: 0, subscriptions: 0, mrr: 0, topups: 0 };
    }

    const mostRecent = metrics[metrics.length - 1];
    // Look for previous day in allMetrics (which includes the extra day)
    const mostRecentDate = new Date(mostRecent.date);
    const prevDate = new Date(mostRecentDate);
    prevDate.setDate(prevDate.getDate() - 1);
    const prevDateStr = prevDate.toISOString().split('T')[0];
    const previousDay = allMetrics.find((m) => m.date === prevDateStr);

    if (!previousDay) {
      return { signups: 0, datasets: 0, emails: 0, credits: 0, subscriptions: 0, mrr: 0, topups: 0 };
    }

    return {
      signups: calculateChange(mostRecent.signups, previousDay.signups),
      datasets: calculateChange(mostRecent.datasets_created, previousDay.datasets_created),
      emails: calculateChange(mostRecent.emails_sent, previousDay.emails_sent),
      credits: calculateChange(mostRecent.credits_used, previousDay.credits_used),
      subscriptions: calculateChange(
        mostRecent.subscriptions?.total || 0,
        previousDay.subscriptions?.total || 0
      ),
      mrr: calculateChange(mostRecent.mrr || 0, previousDay.mrr || 0),
      topups: calculateChange(mostRecent.topups || 0, previousDay.topups || 0),
    };
  }, [metrics, allMetrics]);

  // Calculate cumulative metrics with proper typing
  const cumulativeMetrics = useMemo<CumulativeMetric[]>(() => {
    // Sort by date ascending (oldest first) to calculate cumulative correctly
    const sortedMetrics = [...metrics].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    let runningSignups = 0;
    let runningDatasets = 0;
    let runningEmails = 0;
    let runningCredits = 0;
    let runningSubscriptions = { total: 0, starter: 0, pro: 0, growth: 0, enterprise: 0 };
    let runningMrr = 0;
    let runningTopups = 0;

    const cumulative: CumulativeMetric[] = sortedMetrics.map((day) => {
      runningSignups += day.signups;
      runningDatasets += day.datasets_created;
      runningEmails += day.emails_sent;
      runningCredits += day.credits_used;
      runningSubscriptions = {
        total: runningSubscriptions.total + (day.subscriptions?.total || 0),
        starter: runningSubscriptions.starter + (day.subscriptions?.starter || 0),
        pro: runningSubscriptions.pro + (day.subscriptions?.pro || 0),
        growth: runningSubscriptions.growth + (day.subscriptions?.growth || 0),
        enterprise: runningSubscriptions.enterprise + (day.subscriptions?.enterprise || 0),
      };
      runningMrr += day.mrr || 0;
      runningTopups += day.topups || 0;

      return {
        ...day,
        cumulative_signups: runningSignups,
        cumulative_datasets: runningDatasets,
        cumulative_emails: runningEmails,
        cumulative_credits: runningCredits,
        cumulative_subscriptions: { ...runningSubscriptions },
        cumulative_mrr: runningMrr,
        cumulative_topups: runningTopups,
      };
    });

    // Reverse to show newest first in the table
    return cumulative.reverse();
  }, [metrics]);

  // Create date-indexed maps for O(1) previous-day lookup
  // Use allMetrics (not metrics) to include the extra day for comparison
  const metricsMap = useMemo(() => {
    const map = new Map<string, DailyMetric>();
    allMetrics.forEach((m) => map.set(m.date, m));
    return map;
  }, [allMetrics]);

  const cumulativeMetricsMap = useMemo(() => {
    const map = new Map<string, CumulativeMetric>();
    cumulativeMetrics.forEach((m) => map.set(m.date, m));
    return map;
  }, [cumulativeMetrics]);

  // Helper to get previous day (one day before given date)
  const getPreviousDay = <T extends DailyMetric | CumulativeMetric>(currentDate: string, map: Map<string, T>): T | undefined => {
    const date = new Date(currentDate);
    // Go back up to 7 days to find the previous data point (handles weekends/gaps)
    for (let i = 1; i <= 7; i++) {
      const prevDate = new Date(date);
      prevDate.setDate(prevDate.getDate() - i);
      const prevDateStr = prevDate.toISOString().split('T')[0];
      const prevDay = map.get(prevDateStr);
      if (prevDay) {
        return prevDay;
      }
    }
    return undefined;
  };

  // Helper function to render change indicator
  const renderChangeIndicator = (change: number) => {
    const absChange = Math.abs(change);
    const formattedChange = absChange.toFixed(1);

    if (change > 0) {
      return (
        <div className="flex items-center gap-1 text-xs text-green-600 font-medium">
          <TrendingUp className="w-3 h-3" />
          <span>+{formattedChange}%</span>
        </div>
      );
    } else if (change < 0) {
      return (
        <div className="flex items-center gap-1 text-xs text-red-600 font-medium">
          <TrendingDown className="w-3 h-3" />
          <span>{formattedChange}%</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1 text-xs text-neutral-500 font-medium">
          <Minus className="w-3 h-3" />
          <span>0%</span>
        </div>
      );
    }
  };

  // Helper function to render value with subtle change badge
  const renderValueWithChange = (
    value: number,
    previous: number | null | undefined,
    color: string
  ) => {
    // If previous is null or undefined (not just 0), don't show change
    if (previous === null || previous === undefined) {
      return <span className={`text-sm font-semibold ${color}`}>{value.toLocaleString()}</span>;
    }

    let change: number;
    if (previous === 0) {
      change = value === 0 ? 0 : 100;
    } else {
      change = ((value - previous) / previous) * 100;
    }

    const absChange = Math.abs(change);

    let badgeColor = 'bg-neutral-100 text-neutral-600';
    let icon = null;

    if (change > 5) {
      badgeColor = 'bg-green-100 text-green-700';
      icon = <TrendingUp className="w-2.5 h-2.5" />;
    } else if (change < -5) {
      badgeColor = 'bg-red-100 text-red-700';
      icon = <TrendingDown className="w-2.5 h-2.5" />;
    }

    return (
      <div className="flex items-center gap-2">
        <span className={`text-sm font-semibold ${color}`}>{value.toLocaleString()}</span>
        {Math.abs(change) > 0.1 && (
          <span
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${badgeColor}`}
          >
            {icon}
            {change > 0 ? '+' : ''}{absChange >= 100 ? '100+' : absChange.toFixed(0)}%
          </span>
        )}
      </div>
    );
  };

  // Helper function to render tier breakdown badges
  const renderTierBadges = (subscriptions: SubscriptionsByTier, compact: boolean = true) => {
    const tiers = [
      { key: 'starter', label: 'S', fullLabel: 'Starter', color: 'bg-blue-100 text-blue-700' },
      { key: 'pro', label: 'P', fullLabel: 'Pro', color: 'bg-purple-100 text-purple-700' },
      { key: 'growth', label: 'G', fullLabel: 'Growth', color: 'bg-amber-100 text-amber-700' },
      { key: 'enterprise', label: 'E', fullLabel: 'Enterprise', color: 'bg-emerald-100 text-emerald-700' },
    ] as const;

    const nonZeroTiers = tiers.filter((tier) => (subscriptions?.[tier.key] || 0) > 0);

    if (nonZeroTiers.length === 0) {
      return <span className="text-neutral-400 text-xs">-</span>;
    }

    return (
      <div className="flex gap-1 flex-wrap">
        {nonZeroTiers.map((tier) => (
          <span
            key={tier.key}
            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${tier.color}`}
            title={`${tier.fullLabel}: ${subscriptions?.[tier.key] || 0}`}
          >
            {compact ? tier.label : tier.fullLabel}:{subscriptions?.[tier.key] || 0}
          </span>
        ))}
      </div>
    );
  };

  // Helper function to format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Build chart data arrays — ascending order for correct line rendering
  const chartData = useMemo(() => {
    const ascendingMetrics = [...metrics].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    const ascendingCumulative = [...cumulativeMetrics].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    const source = viewMode === 'daily' ? ascendingMetrics : ascendingCumulative;

    return {
      signups: source.map((d) => ({
        dateLabel: formatDateLabel(d.date),
        rawDate: d.date,
        value:
          viewMode === 'daily'
            ? (d as DailyMetric).signups
            : (d as CumulativeMetric).cumulative_signups,
      })),
      datasets: source.map((d) => ({
        dateLabel: formatDateLabel(d.date),
        rawDate: d.date,
        value:
          viewMode === 'daily'
            ? (d as DailyMetric).datasets_created
            : (d as CumulativeMetric).cumulative_datasets,
      })),
      emails: source.map((d) => ({
        dateLabel: formatDateLabel(d.date),
        rawDate: d.date,
        value:
          viewMode === 'daily'
            ? (d as DailyMetric).emails_sent
            : (d as CumulativeMetric).cumulative_emails,
      })),
      credits: source.map((d) => ({
        dateLabel: formatDateLabel(d.date),
        rawDate: d.date,
        value:
          viewMode === 'daily'
            ? (d as DailyMetric).credits_used
            : (d as CumulativeMetric).cumulative_credits,
      })),
      subscriptions: source.map((d) => ({
        dateLabel: formatDateLabel(d.date),
        rawDate: d.date,
        value:
          viewMode === 'daily'
            ? (d as DailyMetric).subscriptions?.total || 0
            : (d as CumulativeMetric).cumulative_subscriptions?.total || 0,
      })),
      mrr: source.map((d) => ({
        dateLabel: formatDateLabel(d.date),
        rawDate: d.date,
        value:
          viewMode === 'daily'
            ? (d as DailyMetric).mrr || 0
            : (d as CumulativeMetric).cumulative_mrr || 0,
      })),
      topups: source.map((d) => ({
        dateLabel: formatDateLabel(d.date),
        rawDate: d.date,
        value:
          viewMode === 'daily'
            ? (d as DailyMetric).topups || 0
            : (d as CumulativeMetric).cumulative_topups || 0,
      })),
    };
  }, [metrics, cumulativeMetrics, viewMode]);

  // Conditional returns AFTER all hooks
  if (loading) {
    return <Spinner size="lg" text="Loading metrics..." center />;
  }

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">
              {viewMode === 'daily' ? 'Daily Metrics' : 'Cumulative Metrics'}
            </h2>
            <p className="text-neutral-500 mt-0.5 text-sm">System-wide activity metrics</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div
            className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-1"
            role="group"
            aria-label="View mode"
          >
            <button
              onClick={() => setViewMode('daily')}
              aria-pressed={viewMode === 'daily'}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-100 flex items-center gap-2 ${
                viewMode === 'daily'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Daily
            </button>
            <button
              onClick={() => setViewMode('cumulative')}
              aria-pressed={viewMode === 'cumulative'}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-100 flex items-center gap-2 ${
                viewMode === 'cumulative'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              Cumulative
            </button>
          </div>

          {/* Time Range Selector */}
          <select
            id="time-range-select"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="Select time range"
            className="border border-neutral-200 rounded-lg px-4 py-2 bg-white text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-primary-600 transition-all"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* KPI Summary Cards — click to select chart */}
      <div
        className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3"
        role="tablist"
        aria-label="Metric selector"
      >
        {([
          { key: 'signups' as MetricKey, label: viewMode === 'daily' ? 'Recent Signups' : 'Total Signups', value: displayedValues.signups.toLocaleString(), change: rateOfChange.signups, tw: { from: 'from-blue-50', border: 'border-blue-100', borderSelected: 'border-blue-400', text: 'text-blue-700', value: 'text-blue-600', icon: 'text-blue-400', ring: 'ring-blue-200' } },
          { key: 'datasets' as MetricKey, label: viewMode === 'daily' ? 'Recent Datasets' : 'Datasets Created', value: displayedValues.datasets.toLocaleString(), change: rateOfChange.datasets, tw: { from: 'from-purple-50', border: 'border-purple-100', borderSelected: 'border-purple-400', text: 'text-purple-700', value: 'text-purple-600', icon: 'text-purple-400', ring: 'ring-purple-200' } },
          { key: 'emails' as MetricKey, label: viewMode === 'daily' ? 'Recent Emails' : 'Emails Sent', value: displayedValues.emails.toLocaleString(), change: rateOfChange.emails, tw: { from: 'from-green-50', border: 'border-green-100', borderSelected: 'border-green-400', text: 'text-green-700', value: 'text-green-600', icon: 'text-green-400', ring: 'ring-green-200' } },
          { key: 'credits' as MetricKey, label: viewMode === 'daily' ? 'Recent Credits' : 'Credits Used', value: displayedValues.credits.toLocaleString(), change: rateOfChange.credits, tw: { from: 'from-red-50', border: 'border-red-100', borderSelected: 'border-red-400', text: 'text-red-700', value: 'text-red-600', icon: 'text-red-400', ring: 'ring-red-200' } },
          { key: 'subscriptions' as MetricKey, label: viewMode === 'daily' ? 'Subscriptions' : 'Total Subscriptions', value: (displayedValues.subscriptions?.total || 0).toLocaleString(), change: rateOfChange.subscriptions, tw: { from: 'from-amber-50', border: 'border-amber-100', borderSelected: 'border-amber-400', text: 'text-amber-700', value: 'text-amber-600', icon: 'text-amber-400', ring: 'ring-amber-200' } },
          { key: 'mrr' as MetricKey, label: viewMode === 'daily' ? 'New Revenue' : 'Total Revenue', value: formatCurrency(displayedValues.mrr || 0), change: rateOfChange.mrr, tw: { from: 'from-emerald-50', border: 'border-emerald-100', borderSelected: 'border-emerald-400', text: 'text-emerald-700', value: 'text-emerald-600', icon: 'text-emerald-400', ring: 'ring-emerald-200' } },
          { key: 'topups' as MetricKey, label: viewMode === 'daily' ? 'Top-ups' : 'Total Top-ups', value: (displayedValues.topups || 0).toLocaleString(), change: rateOfChange.topups, tw: { from: 'from-cyan-50', border: 'border-cyan-100', borderSelected: 'border-cyan-400', text: 'text-cyan-700', value: 'text-cyan-600', icon: 'text-cyan-400', ring: 'ring-cyan-200' } },
        ]).map((card) => {
          const isSelected = selectedMetric === card.key;
          return (
            <button
              key={card.key}
              role="tab"
              aria-selected={isSelected}
              onClick={() => setSelectedMetric(card.key)}
              className={`bg-gradient-to-br ${card.tw.from} to-white rounded-xl shadow-sm border ${isSelected ? `${card.tw.borderSelected} ring-2 ${card.tw.ring}` : card.tw.border} p-4 hover:shadow-md transition-all duration-75 text-left cursor-pointer`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-semibold ${card.tw.text} truncate`}>
                  {card.label}
                </span>
                <BarChart3 className={`w-3.5 h-3.5 ${card.tw.icon} flex-shrink-0`} />
              </div>
              <div className={`text-2xl font-bold ${card.tw.value} mb-1`}>
                {card.value}
              </div>
              {renderChangeIndicator(card.change)}
            </button>
          );
        })}
      </div>

      {/* Single Line Chart for selected metric */}
      <MetricChart
        title={{ signups: 'Signups', datasets: 'Datasets Created', emails: 'Emails Sent', credits: 'Credits Used', subscriptions: 'Subscriptions', mrr: 'MRR', topups: 'Top-ups' }[selectedMetric]}
        data={chartData[selectedMetric]}
        color={METRIC_COLORS[selectedMetric]}
        isCurrency={selectedMetric === 'mrr'}
      />

      {/* Data Table */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-neutral-50 to-white border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            {viewMode === 'daily' ? 'Daily Breakdown' : 'Cumulative Growth'}
          </h3>
          <p className="text-sm text-neutral-500 mt-0.5">
            {viewMode === 'daily'
              ? 'Day-by-day activity metrics'
              : 'Running totals showing growth over time'}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200" aria-label="Daily metrics data table">
            <thead className="bg-gradient-to-r from-neutral-50 to-neutral-100">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                >
                  Date
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                >
                  Signups
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                >
                  Datasets
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                >
                  Emails
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                >
                  Credits
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                >
                  Subscriptions
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider"
                >
                  Top-ups
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {metrics.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-neutral-500">
                    <div className="flex flex-col items-center">
                      <BarChart3 className="w-12 h-12 text-neutral-300 mb-2" />
                      <p className="text-sm font-medium">No data for this period</p>
                    </div>
                  </td>
                </tr>
              ) : viewMode === 'daily' ? (
                displayMetrics.map((day) => {
                  const prevDailyDay = getPreviousDay(day.date, metricsMap);
                  return (
                    <tr key={day.date} className="hover:bg-blue-50/50 transition-colors duration-75">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                        {formatTableDate(day.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderValueWithChange(day.signups, prevDailyDay?.signups, 'text-blue-600')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderValueWithChange(
                          day.datasets_created,
                          prevDailyDay?.datasets_created,
                          'text-purple-600'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderValueWithChange(
                          day.emails_sent,
                          prevDailyDay?.emails_sent,
                          'text-green-600'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderValueWithChange(
                          day.credits_used,
                          prevDailyDay?.credits_used,
                          'text-red-600'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-amber-600">
                            {day.subscriptions?.total || 0}
                          </span>
                          {renderTierBadges(day.subscriptions)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-cyan-600">
                          {day.topups || 0}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                cumulativeMetrics.map((day) => {
                  const prevCumDay = getPreviousDay(day.date, cumulativeMetricsMap);
                  return (
                    <tr key={day.date} className="hover:bg-blue-50/50 transition-colors duration-75">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                        {formatTableDate(day.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderValueWithChange(
                          day.cumulative_signups,
                          prevCumDay?.cumulative_signups,
                          'text-blue-600'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderValueWithChange(
                          day.cumulative_datasets,
                          prevCumDay?.cumulative_datasets,
                          'text-purple-600'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderValueWithChange(
                          day.cumulative_emails,
                          prevCumDay?.cumulative_emails,
                          'text-green-600'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderValueWithChange(
                          day.cumulative_credits,
                          prevCumDay?.cumulative_credits,
                          'text-red-600'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-amber-600">
                            {day.cumulative_subscriptions?.total || 0}
                          </span>
                          {renderTierBadges(day.cumulative_subscriptions)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-cyan-600">
                          {day.cumulative_topups || 0}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
